/**
 * Shared CMMD auth-failure classification for Forge reconnect / proxy paths.
 *
 * Auth failures are terminal: callers must NOT retry connect/reconnect loops.
 * Transient boot failures (5xx) may retry; 401/403 and typed auth codes must stop.
 */

export const CMMD_AUTH_FAILURE_CODES = [
  "CMMD_AUTH_REQUIRED",
  "CMMD_AUTH_EXPIRED",
  "INVALID_OR_NON_USER_TOKEN",
  "NON_USER_BEARER",
  "CMMD_REST_AUTH_ERROR",
] as const;

export type CmmdAuthFailureCode = (typeof CMMD_AUTH_FAILURE_CODES)[number];

const CMMD_AUTH_FAILURE_CODE_SET: ReadonlySet<string> = new Set(CMMD_AUTH_FAILURE_CODES);

export function readCmmdAuthFailureCode(body: unknown): CmmdAuthFailureCode | null {
  if (!body || typeof body !== "object") return null;
  const code = (body as { readonly code?: unknown }).code;
  if (typeof code !== "string" || !CMMD_AUTH_FAILURE_CODE_SET.has(code)) return null;
  return code as CmmdAuthFailureCode;
}

/**
 * Returns true when an HTTP response (or body-only error) is a terminal CMMD auth
 * failure and must not be retried.
 */
export function isCmmdAuthFailure(status: number | null | undefined, body?: unknown): boolean {
  if (status === 401 || status === 403) return true;
  return readCmmdAuthFailureCode(body) !== null;
}

/**
 * True when a thrown error carries terminal CMMD auth failure metadata
 * (`status` and/or `code`). Used by reconnect loops that catch Error objects.
 */
export function isCmmdAuthFailureError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { readonly status?: unknown }).status;
  const code = (error as { readonly code?: unknown }).code;
  return isCmmdAuthFailure(
    typeof status === "number" ? status : undefined,
    typeof code === "string" ? { code } : undefined,
  );
}

/**
 * Decode a JWT payload without verifying the signature.
 * Used only for fail-closed shape checks before forwarding a Bearer upstream.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const trimmed = token.trim();
  const parts = trimmed.split(".");
  if (parts.length !== 3) return null;
  try {
    const padded = (parts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    const json = Buffer.from(padded, "base64").toString("utf8");
    const payload = JSON.parse(json) as unknown;
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * True when `token` looks like a CMMD *user access* JWT (not a refresh token,
 * MCP grant, runtime ticket, or opaque PAT). Forge must not forward other
 * Bearer classes to forge-context owner APIs.
 */
export function isCmmdUserAccessJwt(token: string | null | undefined): boolean {
  if (!token || token.trim().length === 0) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (payload["type"] === "refresh") return false;
  const userId = payload["userId"];
  if (typeof userId !== "number" || !Number.isFinite(userId)) return false;
  const exp = payload["exp"];
  if (typeof exp === "number" && exp * 1000 <= Date.now()) return false;
  return true;
}
